import removeNullish from '@/helpers/removeNullish';
import { CurrencyValue } from '@/typings/api-models';
import type { Questionnaire } from '@/typings/Questionnaire';
import * as format from '@format';

const isObject = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object';

const onlyIfString = (key: string, value: Record<string, unknown>): string | null => {
  if (key in value) {
    const thisValue = value[key];
    if (typeof thisValue === 'string' && thisValue.length > 0) {
      return thisValue;
    }
  }
  return null;
};

const getDisplayValue = (
  subField: Pick<Questionnaire.Field, 'type' | 'options'>,
  value: unknown,
): string => {
  if (!value) return '—';

  if (subField.type === 'single-select' || subField.type === 'selection') {
    if (Array.isArray(value)) {
      return subField.options?.filter((o) => value.includes(o.key))
        ?.map((o) => o.description ? `${o.label}: ${o.description}` : o.label)
        ?.join(', ') || '';
    }
    return subField.options?.find(o => o.key === value)?.label || '—';
  }

  if (subField.type === 'multi-select') {
    return (value as string[]).map(v => subField.options?.find(o => o.key === v)?.label || '—').join(', ');
  }

  if (subField.type === 'address-us') {

    let address = '';
    if (isObject(value)) {
      address = [
        [
          onlyIfString('line1', value),
          onlyIfString('line2', value),
        ].filter(removeNullish).join(' '),
        onlyIfString('city', value),
        [
          onlyIfString('state', value),
          onlyIfString('zip', value),
        ].filter(removeNullish).join(' '),
      ]
        .filter(removeNullish)
        .join(', ');
      if(address.endsWith(', ')) {
        return '—';
      }
    }
    return address || '—';
  }

  if (subField.type === 'address-international') {
    if (isObject(value)) {
      return ['line1', 'line2', 'city', 'postCode', 'country']
        .map((key) => onlyIfString(key, value))
        .filter(removeNullish)
        .join(', ');
    }
    return '—';
  }

  if (subField.type === 'name') {
    const name = value as {
      firstName: string;
      middleName?: string;
      lastName: string;
    };
    return `${name.firstName}${name.middleName ? ` ${name.middleName} ` : ' '}${name.lastName}` || '—';
  }

  if (subField.type === 'currency') {
    if (typeof value !== 'object' || value === null) return '—';

    const { amount, currency, isUnknown, text } = value as CurrencyValue;

    if (isUnknown) return 'Unknown';
    if (text) return text;
    if (typeof amount === 'number' && typeof currency === 'string') {
      return format.price.withCurrency(amount, currency);
    }

    return '—';
  }

  if (subField.type === 'percent') {
    if (typeof value === 'number') {
      return `${value}%`;
    }

    return '—';
  }

  if (subField.type === 'date') {
    return value && (value instanceof Date || typeof value === 'string' || typeof value === 'number')
      ? new Date(value).toLocaleDateString('default', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
        year: 'numeric',
      })
      : '—';
  }

  if (subField.type === 'add-exemptions') {
    if (!Array.isArray(value)) return '—';

    // Group exemptions by their keys
    const groupedData: Record<string, string[]> = {};
    (value as Array<Record<string, string>>).forEach(item => {
      Object.entries(item).forEach(([key, val]) => {
        if ((key === 'amount' || key === 'exemptionLaws') && val && val.trim() !== '') {
          if (!groupedData[key]) {
            groupedData[key] = [];
          }
          if (!groupedData[key].includes(val)) {
            groupedData[key].push(val);
          }
        }
      });
    });

    // Format the grouped data as a plain string, comma separated
    return (
      (value as Array<Record<string, string>>)
        .filter(item => item.exemptionLaws && item.amount)
        .map(item => `${item.exemptionLaws}: $${Number(item.amount).toLocaleString()}`)
        .join('\n') || '—'
    );

  }

  return String(value);
};

export default getDisplayValue;
